import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { teamsV2API, usersAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ClickableUser } from '@/components/shared/ClickableUser';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Plus, Users, UserPlus, Loader2, MoreVertical, Edit, Trash2, UserMinus, Building2, Palette, Percent, DollarSign, Calculator, AlertTriangle, PieChart, Crown, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { CommissionSimulator } from '@/components/commission/CommissionSimulator';
import { CreateApporteurDialog } from '@/components/users/CreateApporteurDialog';
import { CreateVpDialog } from '@/components/users/CreateVpDialog';

const TEAM_COLORS = [
    { value: '#3B82F6', name: 'Bleu' },
    { value: '#10B981', name: 'Vert' },
    { value: '#F59E0B', name: 'Orange' },
    { value: '#EF4444', name: 'Rouge' },
    { value: '#8B5CF6', name: 'Violet' },
    { value: '#EC4899', name: 'Rose' },
    { value: '#06B6D4', name: 'Cyan' },
    { value: '#84CC16', name: 'Lime' }
];

const ROLE_BADGES = {
    admin_full: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Admin Full' },
    admin: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Admin' },
    vp: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'VP' },
    apporteur: { bg: 'bg-slate-100', text: 'text-slate-800', label: 'Apporteur' }
};

export default function TeamsV2() {
    const { user, isAdminFull, isAdmin, isVP } = useAuth();
    
    // Appeler les fonctions pour obtenir les valeurs booléennes
    const _isAdminFull = isAdminFull();
    const _isAdmin = isAdmin();
    const _isVP = isVP();
    
    const canManage = _isAdminFull || _isAdmin || _isVP;
    // Seuls Admin Full et Admin voient les badges de rôle
    const canSeeRoleBadges = _isAdminFull || _isAdmin;
    
    const [teams, setTeams] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
    const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
    const [simulatorDialogOpen, setSimulatorDialogOpen] = useState(false);
    const [simulatorTeam, setSimulatorTeam] = useState(null);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [createApporteurDialogOpen, setCreateApporteurDialogOpen] = useState(false);
    const [createVpDialogOpen, setCreateVpDialogOpen] = useState(false);
    // Auditeur d'office (Chantier)
    const [auditorDialogOpen, setAuditorDialogOpen] = useState(false);
    const [auditorTeam, setAuditorTeam] = useState(null);
    const [auditorUserId, setAuditorUserId] = useState('none');
    const [savingAuditor, setSavingAuditor] = useState(false);
    
    // Form states
    const [teamForm, setTeamForm] = useState({ 
        name: '', 
        description: '', 
        color: '#3B82F6',
        supervisor_vp_id: '',  // VP Superviseur
        payer_vp_id: '',       // VP Payeur
        commission_split: {
            // Nouveaux champs V2 - % directs sur commission totale
            vpp_pct: 25,       // % direct VP Payeur sur com totale
            vps_pct: 10,       // % direct VP Superviseur sur com totale
            threshold_pct: 30  // Seuil de protection AT Service
        }
    });
    const [selectedUserId, setSelectedUserId] = useState('');
    const [commissionForm, setCommissionForm] = useState({
        commission_percent_ep: 0,
        commission_percent_ch: 0
    });
    
    // Ouvrir le simulateur pour une équipe
    const openSimulator = (team) => {
        setSimulatorTeam(team);
        setSimulatorDialogOpen(true);
    };
    
    // Liste des VP pour les sélecteurs
    const vpUsers = availableUsers.filter(u => u.role === 'vp');
    
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [teamsRes, usersRes] = await Promise.all([
                teamsV2API.getAll(),
                usersAPI.getAll()
            ]);
            setTeams(teamsRes.data || []);
            // Filtrer les utilisateurs disponibles (VP et Apporteurs actifs)
            const activeUsers = (usersRes.data || []).filter(u => 
                u.is_active && ['vp', 'apporteur'].includes(u.role)
            );
            setAvailableUsers(activeUsers);
        } catch (err) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => { loadData(); }, [loadData]);
    
    const handleCreateTeam = async (e) => {
        e.preventDefault();
        if (!teamForm.name.trim()) {
            toast.error('Le nom est obligatoire');
            return;
        }
        // Nouvelle validation V2: vpp_pct + vps_pct <= 100
        const totalPct = (teamForm.commission_split?.vpp_pct || 0) + (teamForm.commission_split?.vps_pct || 0);
        if (totalPct > 100) {
            toast.error('La somme VPP% + VPS% ne peut pas dépasser 100%');
            return;
        }
        setSaving(true);
        try {
            // Préparer les données avec les VP et nouveaux champs V2
            const teamData = {
                name: teamForm.name,
                description: teamForm.description,
                color: teamForm.color,
                supervisor_vp_id: teamForm.supervisor_vp_id || null,
                payer_vp_id: teamForm.payer_vp_id || null,
                commission_split: teamForm.commission_split
            };
            await teamsV2API.create(teamData);
            toast.success('Équipe créée');
            setCreateDialogOpen(false);
            setTeamForm({ 
                name: '', description: '', color: '#3B82F6', 
                supervisor_vp_id: '', payer_vp_id: '',
                commission_split: { 
                    vpp_pct: 25,
                    vps_pct: 10,
                    threshold_pct: 30
                }
            });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };
    
    const handleUpdateTeam = async (e) => {
        e.preventDefault();
        if (!selectedTeam) return;
        // Nouvelle validation V2: vpp_pct + vps_pct <= 100
        const totalPct = (teamForm.commission_split?.vpp_pct || 0) + (teamForm.commission_split?.vps_pct || 0);
        if (totalPct > 100) {
            toast.error('La somme VPP% + VPS% ne peut pas dépasser 100%');
            return;
        }
        setSaving(true);
        try {
            // Préparer les données avec les VP et nouveaux champs V2
            const teamData = {
                name: teamForm.name,
                description: teamForm.description,
                color: teamForm.color,
                supervisor_vp_id: teamForm.supervisor_vp_id || null,
                payer_vp_id: teamForm.payer_vp_id || null,
                commission_split: teamForm.commission_split
            };
            await teamsV2API.update(selectedTeam.id, teamData);
            toast.success('Équipe mise à jour');
            setEditDialogOpen(false);
            setSelectedTeam(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };
    
    const handleDeleteTeam = async (teamId) => {
        if (!window.confirm('Supprimer cette équipe ?')) return;
        try {
            await teamsV2API.delete(teamId);
            toast.success('Équipe supprimée');
            loadData();
        } catch (err) {
            toast.error('Erreur');
        }
    };
    
    const handleAddMember = async (e) => {
        e.preventDefault();
        if (!selectedTeam || !selectedUserId) return;
        setSaving(true);
        try {
            await teamsV2API.addMember(selectedTeam.id, { user_id: selectedUserId, role_in_team: 'member' });
            toast.success('Membre ajouté');
            setAddMemberDialogOpen(false);
            setSelectedUserId('');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };
    
    const handleRemoveMember = async (teamId, userId) => {
        if (!window.confirm('Retirer ce membre de l\'équipe ?')) return;
        try {
            await teamsV2API.removeMember(teamId, userId);
            toast.success('Membre retiré');
            loadData();
        } catch (err) {
            toast.error('Erreur');
        }
    };
    
    const openEditDialog = (team) => {
        setSelectedTeam(team);
        const existingSplit = team.commission_split || {};
        setTeamForm({ 
            name: team.name, 
            description: team.description || '', 
            color: team.color || '#3B82F6',
            supervisor_vp_id: team.supervisor_vp_id || '',
            payer_vp_id: team.payer_vp_id || '',
            commission_split: {
                // Nouveaux champs V2 - % directs sur commission totale
                vpp_pct: existingSplit.vpp_pct ?? existingSplit.payer_percent ?? 25,
                vps_pct: existingSplit.vps_pct ?? existingSplit.supervisor_percent ?? 10,
                threshold_pct: existingSplit.threshold_pct ?? 30
            }
        });
        setEditDialogOpen(true);
    };
    
    const openAddMemberDialog = (team) => {
        setSelectedTeam(team);
        setSelectedUserId('');
        setAddMemberDialogOpen(true);
    };
    
    // ============== AUDITEUR D'OFFICE (CHANTIER) ==============
    const openAuditorDialog = (team) => {
        setAuditorTeam(team);
        setAuditorUserId(team.chantier_default_auditor_id || 'none');
        setAuditorDialogOpen(true);
    };
    
    const handleSaveAuditor = async () => {
        if (!auditorTeam) return;
        setSavingAuditor(true);
        try {
            const auditeur_id = auditorUserId === 'none' ? null : auditorUserId;
            const res = await teamsV2API.setChantierAuditor(auditorTeam.id, auditeur_id);
            const n = res.data?.backfilled || 0;
            if (auditeur_id) {
                toast.success(`Auditeur d'office défini. ${n} dossier(s) Chantier existant(s) mis à jour.`);
            } else {
                toast.success("Auditeur d'office retiré pour cette équipe.");
            }
            setAuditorDialogOpen(false);
            setAuditorTeam(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSavingAuditor(false);
        }
    };
    
    // ============== GESTION DES COMMISSIONS MEMBRES ==============
    
    const openCommissionDialog = async (team, member) => {
        setSelectedTeam(team);
        setSelectedMember(member);
        
        // Charger la commission actuelle du membre
        try {
            const res = await teamsV2API.getMemberCommission(team.id, member.id);
            setCommissionForm({
                commission_percent_ep: res.data.commission_percent_ep || 0,
                commission_percent_ch: res.data.commission_percent_ch || 0
            });
        } catch (err) {
            // Utiliser les valeurs par défaut
            setCommissionForm({
                commission_percent_ep: member.team_commission_ep || 0,
                commission_percent_ch: member.team_commission_ch || 0
            });
        }
        
        setCommissionDialogOpen(true);
    };
    
    const handleUpdateMemberCommission = async (e) => {
        e.preventDefault();
        if (!selectedTeam || !selectedMember) return;
        
        setSaving(true);
        try {
            await teamsV2API.updateMemberCommission(selectedTeam.id, selectedMember.id, commissionForm);
            toast.success('Commission mise à jour');
            setCommissionDialogOpen(false);
            setSelectedMember(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };
    
    // Vérifie si l'utilisateur actuel peut modifier les commissions dans une équipe
    const canEditCommissions = (team) => {
        if (_isAdminFull || _isAdmin) return true;
        // VP Payeur peut modifier les commissions de ses apporteurs
        if (_isVP && team.payer_vp_id === user?.id) return true;
        return false;
    };
    
    // Drag & Drop handler - MISE À JOUR OPTIMISTE sans rechargement
    const handleDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        
        if (!destination) return;
        if (source.droppableId === destination.droppableId) return;
        
        const userId = draggableId;
        const sourceTeamId = source.droppableId === 'unassigned' ? null : source.droppableId;
        const destTeamId = destination.droppableId === 'unassigned' ? null : destination.droppableId;
        
        // MISE À JOUR OPTIMISTE - Modifier l'état local immédiatement
        setTeams(prevTeams => {
            return prevTeams.map(team => {
                // Retirer de l'équipe source
                if (team.id === sourceTeamId) {
                    return {
                        ...team,
                        member_ids: (team.member_ids || []).filter(id => id !== userId)
                    };
                }
                // Ajouter à l'équipe destination
                if (team.id === destTeamId) {
                    return {
                        ...team,
                        member_ids: [...(team.member_ids || []), userId]
                    };
                }
                return team;
            });
        });
        
        // Appeler l'API en arrière-plan
        try {
            // Retirer de l'équipe source si applicable
            if (sourceTeamId) {
                await teamsV2API.removeMember(sourceTeamId, userId);
            }
            // Ajouter à l'équipe destination si applicable
            if (destTeamId) {
                await teamsV2API.addMember(destTeamId, { user_id: userId, role_in_team: 'member' });
            }
            toast.success('Membre déplacé');
            // PAS de rechargement - l'état local est déjà à jour
        } catch (err) {
            toast.error('Erreur lors du déplacement');
            // En cas d'erreur, recharger pour revenir à l'état correct
            loadData();
        }
    };
    
    // Calculer les utilisateurs non assignés à aucune équipe
    const getUnassignedUsers = () => {
        const assignedUserIds = new Set();
        teams.forEach(team => {
            (team.member_ids || []).forEach(id => assignedUserIds.add(id));
            (team.manager_ids || []).forEach(id => assignedUserIds.add(id));
            // Les VP Payeur et Superviseur sont aussi "assignés" à leur équipe
            if (team.payer_vp_id) assignedUserIds.add(team.payer_vp_id);
            if (team.supervisor_vp_id) assignedUserIds.add(team.supervisor_vp_id);
        });
        return availableUsers.filter(u => !assignedUserIds.has(u.id));
    };
    
    // Obtenir les membres d'une équipe
    const getTeamMembers = (team) => {
        // Utiliser team.members qui contient les commissions enrichies par l'API
        // Fallback sur availableUsers si team.members n'existe pas
        if (team.members && team.members.length > 0) {
            return team.members;
        }
        // Fallback: construire depuis availableUsers (sans commissions)
        const memberIds = [...(team.manager_ids || []), ...(team.member_ids || [])];
        return availableUsers.filter(u => memberIds.includes(u.id));
    };
    
    // Obtenir les utilisateurs disponibles pour ajouter à une équipe
    const getAvailableForTeam = (team) => {
        const currentIds = new Set([...(team.manager_ids || []), ...(team.member_ids || [])]);
        return availableUsers.filter(u => !currentIds.has(u.id));
    };
    
    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
            </DashboardLayout>
        );
    }
    
    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="teams-v2-page">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Gestion des Équipes</h1>
                        <p className="text-slate-500 mt-1">
                            Organisez vos équipes et assignez les membres par glisser-déposer
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Bouton Créer VP - Admin/Admin Full uniquement */}
                        {(_isAdminFull || _isAdmin) && (
                            <Button
                                variant="outline"
                                onClick={() => setCreateVpDialogOpen(true)}
                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                data-testid="create-vp-btn"
                            >
                                <Crown className="w-4 h-4 mr-2" />
                                Créer un VP
                            </Button>
                        )}
                        {/* Bouton Créer Apporteur - Admin/Admin Full uniquement */}
                        {(_isAdminFull || _isAdmin) && (
                            <Button 
                                variant="outline" 
                                onClick={() => setCreateApporteurDialogOpen(true)}
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                data-testid="create-apporteur-btn"
                            >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Créer un apporteur
                            </Button>
                        )}
                        {canManage && (
                            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-team-btn">
                                        <Plus className="w-4 h-4 mr-2" />Nouvelle Équipe
                                    </Button>
                                </DialogTrigger>
                            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Créer une équipe</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleCreateTeam} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Nom de l'équipe *</Label>
                                        <Input
                                            value={teamForm.name}
                                            onChange={(e) => setTeamForm(f => ({ ...f, name: e.target.value }))}
                                            placeholder="Ex: Équipe PACA"
                                            required
                                            data-testid="team-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Description</Label>
                                        <Input
                                            value={teamForm.description}
                                            onChange={(e) => setTeamForm(f => ({ ...f, description: e.target.value }))}
                                            placeholder="Description optionnelle"
                                        />
                                    </div>
                                    
                                    {/* Hiérarchie VP */}
                                    <div className="border-t pt-4 space-y-4">
                                        <h4 className="font-medium text-sm text-slate-700">Hiérarchie VP (optionnel)</h4>
                                        
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                VP Superviseur 
                                                <span className="text-xs text-slate-500">(voit tout de l'équipe)</span>
                                            </Label>
                                            <Select
                                                value={teamForm.supervisor_vp_id}
                                                onValueChange={(v) => setTeamForm(f => ({ ...f, supervisor_vp_id: v === 'none' ? '' : v }))}
                                            >
                                                <SelectTrigger data-testid="supervisor-vp-select">
                                                    <SelectValue placeholder="Aucun" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Aucun</SelectItem>
                                                    {vpUsers.map(vp => (
                                                        <SelectItem key={vp.id} value={vp.id}>
                                                            {vp.first_name} {vp.last_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                VP Payeur 
                                                <span className="text-xs text-slate-500">(paye les apporteurs)</span>
                                            </Label>
                                            <Select
                                                value={teamForm.payer_vp_id}
                                                onValueChange={(v) => setTeamForm(f => ({ ...f, payer_vp_id: v === 'none' ? '' : v }))}
                                            >
                                                <SelectTrigger data-testid="payer-vp-select">
                                                    <SelectValue placeholder="Aucun" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Aucun</SelectItem>
                                                    {vpUsers.filter(vp => vp.id !== teamForm.supervisor_vp_id).map(vp => (
                                                        <SelectItem key={vp.id} value={vp.id}>
                                                            {vp.first_name} {vp.last_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        {/* Paramètres Commission V2 - Visible pour Admin Full */}
                                        {_isAdminFull && (
                                            <div className="border-t pt-4 mt-4 space-y-4">
                                                <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                                                    <Calculator className="h-4 w-4" />
                                                    Paramètres Commission V2
                                                </h4>
                                                
                                                {/* % VP sur commission totale (séparément) */}
                                                <div className="space-y-2">
                                                    <Label className="text-xs">
                                                        Commissions VP (% sur commission totale)
                                                    </Label>
                                                    <p className="text-xs text-slate-400 mb-2">
                                                        Définissez le % de chaque VP. La somme = part équipe. Le reste va 50/50 Valnexo/AT Service.
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-1">
                                                            <Label className="text-xs text-emerald-600 font-medium">VP Payeur (%)</Label>
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                step="1"
                                                                value={teamForm.commission_split?.vpp_pct ?? 25}
                                                                onChange={(e) => {
                                                                    const vppPct = parseFloat(e.target.value) || 0;
                                                                    setTeamForm(f => ({
                                                                        ...f,
                                                                        commission_split: {
                                                                            ...f.commission_split,
                                                                            vpp_pct: vppPct
                                                                        }
                                                                    }));
                                                                }}
                                                                data-testid="vpp-pct-input"
                                                            />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <Label className="text-xs text-blue-600 font-medium">VP Superviseur (%)</Label>
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                step="1"
                                                                value={teamForm.commission_split?.vps_pct ?? 10}
                                                                onChange={(e) => {
                                                                    const vpsPct = parseFloat(e.target.value) || 0;
                                                                    setTeamForm(f => ({
                                                                        ...f,
                                                                        commission_split: {
                                                                            ...f.commission_split,
                                                                            vps_pct: vpsPct
                                                                        }
                                                                    }));
                                                                }}
                                                                data-testid="vps-pct-input"
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Barre visuelle et calcul part équipe */}
                                                    <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden flex">
                                                                <div 
                                                                    className="h-full bg-emerald-500 transition-all"
                                                                    style={{ width: `${teamForm.commission_split?.vpp_pct ?? 25}%` }}
                                                                    title="VP Payeur"
                                                                />
                                                                <div 
                                                                    className="h-full bg-blue-500 transition-all"
                                                                    style={{ width: `${teamForm.commission_split?.vps_pct ?? 10}%` }}
                                                                    title="VP Superviseur"
                                                                />
                                                                <div 
                                                                    className="h-full bg-slate-300 transition-all flex-1"
                                                                    title="Reste (Valnexo + AT Service)"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-between text-xs">
                                                            <span className="text-emerald-600">VPP: {teamForm.commission_split?.vpp_pct ?? 25}%</span>
                                                            <span className="text-blue-600">VPS: {teamForm.commission_split?.vps_pct ?? 10}%</span>
                                                            <span className="font-semibold">
                                                                Part Équipe = {((teamForm.commission_split?.vpp_pct ?? 25) + (teamForm.commission_split?.vps_pct ?? 10)).toFixed(0)}%
                                                            </span>
                                                            <span className="text-slate-500">
                                                                Reste: {(100 - (teamForm.commission_split?.vpp_pct ?? 25) - (teamForm.commission_split?.vps_pct ?? 10)).toFixed(0)}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Avertissement si > 100% */}
                                                    {((teamForm.commission_split?.vpp_pct ?? 0) + (teamForm.commission_split?.vps_pct ?? 0)) > 100 && (
                                                        <div className="flex items-center gap-2 text-red-600 text-xs">
                                                            <AlertTriangle className="h-4 w-4" />
                                                            La somme VPP + VPS ne peut pas dépasser 100%
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {/* Seuil de protection AT Service */}
                                                <div className="space-y-2">
                                                    <Label className="text-xs flex items-center gap-2">
                                                        Seuil AT Service (%)
                                                        <span className="text-slate-400">Si Part Équipe &gt; Seuil, l'excès réduit Valnexo</span>
                                                    </Label>
                                                    <div className="flex items-center gap-3">
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="1"
                                                            className="w-24"
                                                            value={teamForm.commission_split?.threshold_pct ?? 30}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value) || 0;
                                                                setTeamForm(f => ({
                                                                    ...f,
                                                                    commission_split: {
                                                                        ...f.commission_split,
                                                                        threshold_pct: val
                                                                    }
                                                                }));
                                                            }}
                                                            data-testid="threshold-pct-input"
                                                        />
                                                        <span className="text-sm text-slate-600">%</span>
                                                        {((teamForm.commission_split?.vpp_pct ?? 25) + (teamForm.commission_split?.vps_pct ?? 10)) > (teamForm.commission_split?.threshold_pct ?? 30) && (
                                                            <span className="flex items-center gap-1 text-amber-600 text-xs">
                                                                <AlertTriangle className="h-3 w-3" />
                                                                Seuil dépassé !
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label>Couleur</Label>
                                        <div className="flex gap-2 flex-wrap">
                                            {TEAM_COLORS.map(c => (
                                                <button
                                                    key={c.value}
                                                    type="button"
                                                    className={cn(
                                                        "w-8 h-8 rounded-full border-2 transition-all",
                                                        teamForm.color === c.value ? "border-slate-900 scale-110" : "border-transparent"
                                                    )}
                                                    style={{ backgroundColor: c.value }}
                                                    onClick={() => setTeamForm(f => ({ ...f, color: c.value }))}
                                                    title={c.name}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button type="submit" disabled={saving} data-testid="save-team-btn">
                                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                            Créer l'équipe
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                        )}
                    </div>
                </div>
                
                {/* Dialog Créer Apporteur (VP) */}
                <CreateApporteurDialog
                    open={createApporteurDialogOpen}
                    onOpenChange={setCreateApporteurDialogOpen}
                    onCreated={loadData}
                />

                {/* Dialog Créer VP (Admin) */}
                <CreateVpDialog
                    open={createVpDialogOpen}
                    onOpenChange={setCreateVpDialogOpen}
                    onCreated={loadData}
                />
                
                {/* Stats Cards */}
                <div className="grid grid-cols-3 md:grid-cols-3 gap-3">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                    <Building2 className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Équipes</p>
                                    <p className="text-2xl font-bold">{teams.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                    <Users className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Membres assignés</p>
                                    <p className="text-2xl font-bold">
                                        {availableUsers.length - getUnassignedUsers().length}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                                    <UserPlus className="w-6 h-6 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Non assignés</p>
                                    <p className="text-2xl font-bold">{getUnassignedUsers().length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                
                {/* Drag & Drop Area */}
                <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        {/* Unassigned Users */}
                        <Card className="lg:col-span-1">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Users className="w-4 h-4 text-slate-500" />
                                    Non assignés
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Droppable droppableId="unassigned">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={cn(
                                                "min-h-[200px] p-2 rounded-lg border-2 border-dashed transition-colors",
                                                snapshot.isDraggingOver ? "border-blue-400 bg-blue-50" : "border-slate-200"
                                            )}
                                        >
                                            {getUnassignedUsers().map((member, index) => (
                                                <Draggable key={member.id} draggableId={member.id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className={cn(
                                                                "p-3 mb-2 rounded-lg border bg-white shadow-sm cursor-grab",
                                                                snapshot.isDragging && "shadow-lg ring-2 ring-blue-400"
                                                            )}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
                                                                    {member.first_name?.[0]}{member.last_name?.[0]}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="font-medium text-sm truncate">
                                                                        <ClickableUser userId={member.id} name={`${member.first_name} ${member.last_name}`} />
                                                                    </p>
                                                                    {/* Badge de rôle uniquement pour Admin Full et Admin */}
                                                                    {canSeeRoleBadges && (
                                                                        <Badge className={cn("text-xs", ROLE_BADGES[member.role]?.bg, ROLE_BADGES[member.role]?.text)}>
                                                                            {ROLE_BADGES[member.role]?.label}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            {getUnassignedUsers().length === 0 && (
                                                <p className="text-center text-slate-400 text-sm py-8">
                                                    Tous les membres sont assignés
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </Droppable>
                            </CardContent>
                        </Card>
                        
                        {/* Teams Grid */}
                        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {teams.map(team => (
                                <Card key={team.id} className="border-t-4" style={{ borderTopColor: team.color }}>
                                    <CardHeader className="pb-2">
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="text-base flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: team.color }} />
                                                {team.name}
                                            </CardTitle>
                                            {canManage && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => openSimulator(team)}>
                                                            <PieChart className="w-4 h-4 mr-2" />Simulateur commission
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem onClick={() => openAddMemberDialog(team)}>
                                                            <UserPlus className="w-4 h-4 mr-2" />Ajouter membre
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => openEditDialog(team)}>
                                                            <Edit className="w-4 h-4 mr-2" />Modifier
                                                        </DropdownMenuItem>
                                                        {(_isAdminFull || _isAdmin) && (
                                                            <DropdownMenuItem onClick={() => openAuditorDialog(team)} data-testid={`team-auditor-menu-${team.id}`}>
                                                                <ShieldCheck className="w-4 h-4 mr-2" />Auditeur d'office (Chantier)
                                                            </DropdownMenuItem>
                                                        )}
                                                        {(_isAdminFull || _isAdmin) && (
                                                            <DropdownMenuItem 
                                                                onClick={() => handleDeleteTeam(team.id)}
                                                                className="text-red-600"
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />Supprimer
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </div>
                                        {team.description && (
                                            <p className="text-xs text-slate-500">{team.description}</p>
                                        )}
                                        {/* Afficher les VP selon les règles de visibilité */}
                                        {/* Pour Apporteur: affiche "VP: nom" (pas "Payeur") */}
                                        {/* Pour Admin/Admin Full: affiche Superviseur ET Payeur */}
                                        {/* Pour VP Payeur: ne voit pas le Superviseur */}
                                        {(team.supervisor_vp_name || team.payer_vp_name || team.vp_name) && (
                                            <div className="mt-2 space-y-1 text-xs">
                                                {/* Pour Admin/Admin Full: affiche les deux VP */}
                                                {(_isAdminFull || _isAdmin) && team.supervisor_vp_name && (
                                                    <div className="flex items-center gap-1 text-amber-700">
                                                        <span className="font-medium">Superviseur:</span> {team.supervisor_vp_name}
                                                    </div>
                                                )}
                                                {(_isAdminFull || _isAdmin) && team.payer_vp_name && (
                                                    <div className="flex items-center gap-1 text-emerald-700">
                                                        <span className="font-medium">Payeur:</span> {team.payer_vp_name}
                                                    </div>
                                                )}
                                                {/* Pour VP: affiche seulement le Payeur (pas Superviseur) */}
                                                {!_isAdminFull && !_isAdmin && _isVP && team.payer_vp_name && (
                                                    <div className="flex items-center gap-1 text-emerald-700">
                                                        <span className="font-medium">VP Payeur:</span> {team.payer_vp_name}
                                                    </div>
                                                )}
                                                {/* Pour Apporteur: affiche "VP" sans "Payeur" */}
                                                {!_isAdminFull && !_isAdmin && !_isVP && team.vp_name && (
                                                    <div className="flex items-center gap-1 text-emerald-700">
                                                        <span className="font-medium">VP:</span> {team.vp_name}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {(_isAdminFull || _isAdmin) && team.chantier_default_auditor_name && (
                                            <div className="mt-2 flex items-center gap-1 text-xs text-indigo-700" data-testid={`team-auditor-label-${team.id}`}>
                                                <ShieldCheck className="w-3.5 h-3.5" />
                                                <span className="font-medium">Auditeur d'office (Chantier):</span> {team.chantier_default_auditor_name}
                                            </div>
                                        )}
                                    </CardHeader>
                                    <CardContent>
                                        <Droppable droppableId={team.id}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={cn(
                                                        "min-h-[150px] p-2 rounded-lg border-2 border-dashed transition-colors",
                                                        snapshot.isDraggingOver ? "border-blue-400 bg-blue-50" : "border-slate-200"
                                                    )}
                                                >
                                                    {getTeamMembers(team).map((member, index) => {
                                                        const isManager = (team.manager_ids || []).includes(member.id);
                                                        const isVpPayer = member.is_vp_payer === true;
                                                        const isVpSupervisor = member.is_vp_supervisor === true;
                                                        
                                                        // VP Superviseur - affiché statiquement avec fond ambre (Full Admin uniquement)
                                                        if (isVpSupervisor) {
                                                            return (
                                                                <div
                                                                    key={member.id}
                                                                    className={cn(
                                                                        "p-3 mb-2 rounded-lg border bg-amber-50 shadow-sm group border-l-4 border-l-amber-500"
                                                                    )}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <div 
                                                                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white bg-amber-500"
                                                                        >
                                                                            {member.first_name?.[0]}{member.last_name?.[0]}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-medium text-sm truncate">
                                                                                <ClickableUser userId={member.id} name={`${member.first_name} ${member.last_name}`} />
                                                                            </p>
                                                                            <div className="flex items-center gap-2">
                                                                                <Badge className="text-xs bg-amber-100 text-amber-800">
                                                                                    VP Superviseur
                                                                                </Badge>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        
                                                        // VP Payeur - affiché statiquement avec fond vert
                                                        if (isVpPayer) {
                                                            return (
                                                                <div
                                                                    key={member.id}
                                                                    className={cn(
                                                                        "p-3 mb-2 rounded-lg border bg-emerald-50 shadow-sm group border-l-4"
                                                                    )}
                                                                    style={{ borderLeftColor: team.color }}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <div 
                                                                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white"
                                                                            style={{ backgroundColor: team.color }}
                                                                        >
                                                                            {member.first_name?.[0]}{member.last_name?.[0]}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-medium text-sm truncate">
                                                                                <ClickableUser userId={member.id} name={`${member.first_name} ${member.last_name}`} />
                                                                            </p>
                                                                            <div className="flex items-center gap-2">
                                                                                <Badge className="text-xs bg-emerald-100 text-emerald-800">
                                                                                    VP Payeur
                                                                                </Badge>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        
                                                        return (
                                                            <Draggable key={member.id} draggableId={member.id} index={index}>
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={cn(
                                                                            "p-3 mb-2 rounded-lg border bg-white shadow-sm cursor-grab group",
                                                                            snapshot.isDragging && "shadow-lg ring-2 ring-blue-400",
                                                                            isManager && "border-l-4"
                                                                        )}
                                                                        style={{
                                                                            ...provided.draggableProps.style,
                                                                            borderLeftColor: isManager ? team.color : undefined
                                                                        }}
                                                                    >
                                                                        <div className="flex items-center gap-3">
                                                                            <div 
                                                                                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white"
                                                                                style={{ backgroundColor: team.color }}
                                                                            >
                                                                                {member.first_name?.[0]}{member.last_name?.[0]}
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <p className="font-medium text-sm truncate">
                                                                                    <ClickableUser userId={member.id} name={`${member.first_name} ${member.last_name}`} />
                                                                                    {isManager && <span className="ml-1 text-xs text-slate-500">(Manager)</span>}
                                                                                </p>
                                                                                <div className="flex items-center gap-2">
                                                                                    {/* Badge de rôle uniquement pour Admin Full et Admin */}
                                                                                    {canSeeRoleBadges && (
                                                                                        <Badge className={cn("text-xs", ROLE_BADGES[member.role]?.bg, ROLE_BADGES[member.role]?.text)}>
                                                                                            {ROLE_BADGES[member.role]?.label}
                                                                                        </Badge>
                                                                                    )}
                                                                                    {/* Afficher les commissions pour Admin Full et Admin UNIQUEMENT (pas VP) */}
                                                                                    {member.role === 'apporteur' && (_isAdminFull || _isAdmin) && (
                                                                                        <span className="text-xs text-slate-400">
                                                                                            EP: {member.team_commission_ep || 0}% | CH: {member.team_commission_ch || 0}%
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            {/* Menu d'actions sur le membre */}
                                                                            {canManage && (
                                                                                <DropdownMenu>
                                                                                    <DropdownMenuTrigger asChild>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="icon"
                                                                                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                        >
                                                                                            <MoreVertical className="w-3 h-3" />
                                                                                        </Button>
                                                                                    </DropdownMenuTrigger>
                                                                                    <DropdownMenuContent align="end">
                                                                                        {/* Éditer commission (Admin + VP Payeur + Apporteur uniquement) */}
                                                                                        {member.role === 'apporteur' && canEditCommissions(team) && (
                                                                                            <DropdownMenuItem onClick={() => openCommissionDialog(team, member)}>
                                                                                                <Percent className="w-4 h-4 mr-2" />
                                                                                                Modifier commission
                                                                                            </DropdownMenuItem>
                                                                                        )}
                                                                                        <DropdownMenuSeparator />
                                                                                        <DropdownMenuItem 
                                                                                            onClick={() => handleRemoveMember(team.id, member.id)}
                                                                                            className="text-red-600"
                                                                                        >
                                                                                            <UserMinus className="w-4 h-4 mr-2" />
                                                                                            Retirer de l'équipe
                                                                                        </DropdownMenuItem>
                                                                                    </DropdownMenuContent>
                                                                                </DropdownMenu>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                    {getTeamMembers(team).length === 0 && (
                                                        <p className="text-center text-slate-400 text-sm py-6">
                                                            Glissez des membres ici
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </Droppable>
                                        <div className="mt-2 text-xs text-slate-500 text-center">
                                            {getTeamMembers(team).length} membre{getTeamMembers(team).length !== 1 ? 's' : ''}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                            
                            {teams.length === 0 && (
                                <Card className="lg:col-span-3 p-12 text-center">
                                    <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                                    <p className="text-slate-500">Aucune équipe créée</p>
                                    <p className="text-sm text-slate-400 mt-1">
                                        Créez votre première équipe pour commencer
                                    </p>
                                </Card>
                            )}
                        </div>
                    </div>
                </DragDropContext>
                
                {/* Edit Team Dialog */}
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Modifier l'équipe</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleUpdateTeam} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nom de l'équipe *</Label>
                                <Input
                                    value={teamForm.name}
                                    onChange={(e) => setTeamForm(f => ({ ...f, name: e.target.value }))}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Input
                                    value={teamForm.description}
                                    onChange={(e) => setTeamForm(f => ({ ...f, description: e.target.value }))}
                                />
                            </div>
                            
                            {/* Hiérarchie VP */}
                            <div className="border-t pt-4 space-y-4">
                                <h4 className="font-medium text-sm text-slate-700">Hiérarchie VP</h4>
                                
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        VP Superviseur 
                                        <span className="text-xs text-slate-500">(voit tout)</span>
                                    </Label>
                                    <Select
                                        value={teamForm.supervisor_vp_id || 'none'}
                                        onValueChange={(v) => setTeamForm(f => ({ ...f, supervisor_vp_id: v === 'none' ? '' : v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Aucun" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Aucun</SelectItem>
                                            {vpUsers.map(vp => (
                                                <SelectItem key={vp.id} value={vp.id}>
                                                    {vp.first_name} {vp.last_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        VP Payeur 
                                        <span className="text-xs text-slate-500">(paye les apporteurs)</span>
                                    </Label>
                                    <Select
                                        value={teamForm.payer_vp_id || 'none'}
                                        onValueChange={(v) => setTeamForm(f => ({ ...f, payer_vp_id: v === 'none' ? '' : v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Aucun" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Aucun</SelectItem>
                                            {vpUsers.filter(vp => vp.id !== teamForm.supervisor_vp_id).map(vp => (
                                                <SelectItem key={vp.id} value={vp.id}>
                                                    {vp.first_name} {vp.last_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            {/* Paramètres Commission V2 - Visible pour Admin Full */}
                            {_isAdminFull && (
                                <div className="border-t pt-4 mt-4 space-y-4">
                                    <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                                        <Calculator className="h-4 w-4" />
                                        Paramètres Commission V2
                                    </h4>
                                    
                                    {/* % VP sur commission totale (séparément) */}
                                    <div className="space-y-2">
                                        <Label className="text-xs">
                                            Commissions VP (% sur commission totale)
                                        </Label>
                                        <p className="text-xs text-slate-400 mb-2">
                                            Définissez le % de chaque VP. La somme = part équipe.
                                        </p>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <Label className="text-xs text-emerald-600 font-medium">VP Payeur (%)</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    value={teamForm.commission_split?.vpp_pct ?? 25}
                                                    onChange={(e) => {
                                                        const vppPct = parseFloat(e.target.value) || 0;
                                                        setTeamForm(f => ({
                                                            ...f,
                                                            commission_split: {
                                                                ...f.commission_split,
                                                                vpp_pct: vppPct
                                                            }
                                                        }));
                                                    }}
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-blue-600 font-medium">VP Superviseur (%)</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    value={teamForm.commission_split?.vps_pct ?? 10}
                                                    onChange={(e) => {
                                                        const vpsPct = parseFloat(e.target.value) || 0;
                                                        setTeamForm(f => ({
                                                            ...f,
                                                            commission_split: {
                                                                ...f.commission_split,
                                                                vps_pct: vpsPct
                                                            }
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Résumé */}
                                        <div className="text-xs text-slate-600 mt-2">
                                            Part Équipe = <span className="font-semibold">{((teamForm.commission_split?.vpp_pct ?? 25) + (teamForm.commission_split?.vps_pct ?? 10)).toFixed(0)}%</span>
                                        </div>
                                        
                                        {/* Avertissement si > 100% */}
                                        {((teamForm.commission_split?.vpp_pct ?? 0) + (teamForm.commission_split?.vps_pct ?? 0)) > 100 && (
                                            <div className="flex items-center gap-2 text-red-600 text-xs">
                                                <AlertTriangle className="h-4 w-4" />
                                                La somme VPP + VPS ne peut pas dépasser 100%
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Seuil de protection AT Service */}
                                    <div className="space-y-2">
                                        <Label className="text-xs flex items-center gap-2">
                                            Seuil AT Service (%)
                                        </Label>
                                        <div className="flex items-center gap-3">
                                            <Input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="1"
                                                className="w-24"
                                                value={teamForm.commission_split?.threshold_pct ?? 30}
                                                onChange={(e) => {
                                                    const val = parseFloat(e.target.value) || 0;
                                                    setTeamForm(f => ({
                                                        ...f,
                                                        commission_split: {
                                                            ...f.commission_split,
                                                            threshold_pct: val
                                                        }
                                                    }));
                                                }}
                                            />
                                            <span className="text-sm text-slate-600">%</span>
                                            {((teamForm.commission_split?.vpp_pct ?? 25) + (teamForm.commission_split?.vps_pct ?? 10)) > (teamForm.commission_split?.threshold_pct ?? 30) && (
                                                <span className="flex items-center gap-1 text-amber-600 text-xs">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    Seuil dépassé
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label>Couleur</Label>
                                <div className="flex gap-2 flex-wrap">
                                    {TEAM_COLORS.map(c => (
                                        <button
                                            key={c.value}
                                            type="button"
                                            className={cn(
                                                "w-8 h-8 rounded-full border-2 transition-all",
                                                teamForm.color === c.value ? "border-slate-900 scale-110" : "border-transparent"
                                            )}
                                            style={{ backgroundColor: c.value }}
                                            onClick={() => setTeamForm(f => ({ ...f, color: c.value }))}
                                        />
                                    ))}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={saving}>
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Enregistrer
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                
                {/* Add Member Dialog */}
                <Dialog open={auditorDialogOpen} onOpenChange={setAuditorDialogOpen}>
                    <DialogContent data-testid="team-auditor-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                                Auditeur d'office (Chantier) — {auditorTeam?.name}
                            </DialogTitle>
                            <DialogDescription>
                                L'auditeur choisi sera désigné automatiquement sur tous les dossiers
                                <strong> Chantier</strong> de cette équipe : les dossiers existants sans
                                auditeur seront rattrapés immédiatement, et tous les futurs dossiers Chantier
                                l'auront d'office. (Easy Power n'est pas concerné.)
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-2 py-2">
                            <Label>Auditeur</Label>
                            <Select value={auditorUserId} onValueChange={setAuditorUserId}>
                                <SelectTrigger data-testid="team-auditor-select">
                                    <SelectValue placeholder="Sélectionner un auditeur" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Aucun (retirer l'auditeur d'office)</SelectItem>
                                    {(() => {
                                        const memberIds = new Set([
                                            ...((auditorTeam?.member_ids) || []),
                                            auditorTeam?.payer_vp_id,
                                            auditorTeam?.supervisor_vp_id,
                                        ].filter(Boolean));
                                        const candidates = availableUsers.filter(u => memberIds.has(u.id));
                                        if (candidates.length === 0) {
                                            return <div className="px-2 py-3 text-xs text-slate-500">Cette équipe n'a pas encore de membre.</div>;
                                        }
                                        return candidates.map(u => (
                                            <SelectItem key={u.id} value={u.id} data-testid={`team-auditor-option-${u.id}`}>
                                                {u.first_name} {u.last_name} · {u.role === 'vp' ? 'VP' : 'Apporteur'}
                                            </SelectItem>
                                        ));
                                    })()}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                Le rôle Auditeur sera activé automatiquement pour la personne choisie.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAuditorDialogOpen(false)} disabled={savingAuditor}>
                                Annuler
                            </Button>
                            <Button onClick={handleSaveAuditor} disabled={savingAuditor} data-testid="team-auditor-save-btn">
                                {savingAuditor && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                Ajouter un membre à {selectedTeam?.name}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddMember} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Sélectionner un membre</Label>
                                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choisir un membre..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {selectedTeam && getAvailableForTeam(selectedTeam).map(u => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.first_name} {u.last_name}{canSeeRoleBadges ? ` (${ROLE_BADGES[u.role]?.label})` : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={saving || !selectedUserId}>
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Ajouter
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                
                {/* Commission Dialog - Modifier la commission d'un apporteur dans l'équipe */}
                <Dialog open={commissionDialogOpen} onOpenChange={setCommissionDialogOpen}>
                    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Percent className="w-5 h-5" />
                                Commission de {selectedMember?.first_name} {selectedMember?.last_name}
                            </DialogTitle>
                            <DialogDescription>
                                Ces commissions sont déduites de la part du VP Payeur et prennent le pas sur les commissions par défaut pour les dossiers de cette équipe.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleUpdateMemberCommission} className="space-y-6">
                            {/* Équipe */}
                            <div className="p-3 bg-slate-50 rounded-lg">
                                <p className="text-sm text-slate-600">
                                    <span className="font-medium">Équipe:</span> {selectedTeam?.name}
                                </p>
                            </div>
                            
                            {/* Commissions */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <DollarSign className="w-4 h-4 text-blue-500" />
                                        Easy Power (%)
                                    </Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={commissionForm.commission_percent_ep}
                                        onChange={(e) => setCommissionForm(f => ({
                                            ...f,
                                            commission_percent_ep: parseFloat(e.target.value) || 0
                                        }))}
                                        data-testid="commission-ep-input"
                                    />
                                    <p className="text-xs text-slate-500">
                                        % de la part VP Payeur
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <DollarSign className="w-4 h-4 text-green-500" />
                                        Chantier (%)
                                    </Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={commissionForm.commission_percent_ch}
                                        onChange={(e) => setCommissionForm(f => ({
                                            ...f,
                                            commission_percent_ch: parseFloat(e.target.value) || 0
                                        }))}
                                        data-testid="commission-ch-input"
                                    />
                                    <p className="text-xs text-slate-500">
                                        % de la part VP Payeur
                                    </p>
                                </div>
                            </div>
                            
                            {/* Info */}
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                                <p className="font-medium mb-1">Comment ça marche ?</p>
                                <p>
                                    La commission de l'apporteur est calculée en % de la part brute du VP Payeur,
                                    puis déduite de celle-ci. Le VP Superviseur garde sa part intacte.
                                </p>
                            </div>
                            
                            <DialogFooter>
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => setCommissionDialogOpen(false)}
                                >
                                    Annuler
                                </Button>
                                <Button type="submit" disabled={saving} data-testid="save-commission-btn">
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Enregistrer
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                
                {/* Simulateur Commission Dialog */}
                <Dialog open={simulatorDialogOpen} onOpenChange={setSimulatorDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <PieChart className="w-5 h-5 text-indigo-600" />
                                Simulateur - {simulatorTeam?.name}
                            </DialogTitle>
                            <DialogDescription>
                                Simulez le calcul des commissions pour cette équipe
                            </DialogDescription>
                        </DialogHeader>
                        {simulatorTeam && (
                            <CommissionSimulator
                                teamConfig={simulatorTeam.commission_split || {}}
                                apporteurPct={50}
                                vppUserId={simulatorTeam.payer_vp_id}
                                vpsUserId={simulatorTeam.supervisor_vp_id}
                                defaultCommission={1000}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
